import { Injectable } from '@angular/core';
import moment from 'moment';
import { map, tap } from 'rxjs/operators';
import { SettingsStore } from './settings.store';
import { AesRequestModel } from '@core/models/AesRequest.model';
import { Observable } from 'rxjs';
import { SessionQuery } from '@state/session/session.query';
import { LibraryService } from '@core/services/library/library.service';
import { SessionService } from '@state/session/session.service';
import {
    LASER_SHOT, SET_APP_COMBINE_FACTOR,
    SET_APP_USER, SET_APP_USER_FORCE,
    SET_CABINET_SKIP,
    SET_CLIENT_ADDRESS_SPLIT,
    SET_CLIENT_CUSTOMFIELDS_ENABLE,
    SET_CLIENT_CUSTOMFIELDS_LABELS,
    SET_CLIENT_CUSTOMFIELDS_TYPE_SELECTED,
    SET_CLIENT_DNI_UNIQUE,
    SET_CLIENT_EDIT_REQUIRED_FIELDS,
    SET_CLIENT_MAIL_UNIQUE,
    SET_CLIENT_NEW_FIELDS,
    SET_CLIENT_NEW_REQUIRED_FIELDS,
    SET_CLIENT_PHONE_UNIQUE,
    SET_CLIENT_PICTURES_DOWNSCALE,
    SET_DIARY_FUTURE_VALIDATE,
    SET_LASER_DCD,
    SET_LASER_SHOT, SET_LASER_SIGN_ENABLED,
    SET_LEGAL_REPRESENTATIVE_AGE, SET_NAME_SPLIT,
    SET_ORDERS,
    SET_PRODUCTS_PACK,
    SET_STOCK,
    SET_SUBSCRIPTIONS, SET_TIMESLOT,
    SET_VALIDATE_APP_CLIENT_REQUIRED_FIELDS,
    UNIQUE_RESTRICTIONS,
    SET_RGPD_DISABLE,
    SET_DOCTOR_VISUAL,
    SET_CLIENT_PRESCRIPTIONS,
    SET_APP_NOTASSISTED,
    SET_APP_NOTASSISTED_FUTURE,
    SET_CLIENT_EDIT_HIDE_FIELDS,
    SET_APP_MAXSERVICES,
    SET_APP_FWA_USER_MODE, SET_VOUCHER_PACK_AUTO, SET_STOCK_TRACEABILITY,
    SET_DIARY_TYPE_DEFAULT,
} from '@core/settings';
import { ClinicService } from '@state/clinic/clinic.service';
import { createClinic } from '@state/clinic/clinic.model';
import { Param } from '@core/models/Param';
import { createCountries } from '../../models/Country';
import { UiService } from '@state/ui/ui.service';
import { TranslateService } from '@ngx-translate/core';
import { createDocumentClass } from '@core/models/DocumentClass';
import { ApiService } from '@core/services/api/api.service';
import {ToastController} from '@ionic/angular';
import { AgendaService } from '../agenda/agenda.service';
import { DOUBLE_VISUAL_AGENDA } from '../agenda/agenda.store';
import { SettingsQuery } from './settings.query';
import { PermissionsStore } from '../permissions/permissions.store';
import { PERM_CREATE_MULTIPLE_APPOINTMENT } from '@core/permissions';

@Injectable({ providedIn: 'root' })
export class SettingsService {

    constructor(
        private agendaService: AgendaService,
        private apiService: ApiService,
        private clinicService: ClinicService,
        private libraryService: LibraryService,
        private permissionsStore: PermissionsStore,
        private sessionQuery: SessionQuery,
        private sessionService: SessionService,
        private settingsStore: SettingsStore,
        private settingsQuery: SettingsQuery,
        private toastController: ToastController,
        private translateService: TranslateService,
        private uiService: UiService) {
    }

    initFlowwwMe(): Observable<void> {
        const form = new AesRequestModel('SEC_FLOWWWME_INIT_FORM', []);

        return this.apiService.callApiObservable(form).pipe(
            map(res => JSON.parse(res)),
            tap(({ data, inf }) => {
                let language = data.Language.toLowerCase();

                // Fix for brazilian portuguese is not an ISO language code
                if (language === 'br') {
                    language = 'pt';
                }

                // Un código sin fichero en assets haría fallar translateService.use()
                // (labels nunca cargadas) y el reload de labels en cada logout
                language = this.libraryService.toShippedLocale(language, 'es');

                moment.locale(language === 'mx' ? 'es-mx' : language);

                this.translateService.use(language).toPromise().then(() => {
                    this.libraryService.loadLabels(language, data.LanguageXML);
                    this.uiService.setLabelsLoaded(true);
                });

                data.__list_5.forEach(c => {
                    this.clinicService.add(createClinic({
                        id: c.clinicid,
                        name: c.clinicname,
                        prefix: c.clinicprefix
                    }));
                });

                this.clinicService.selectClinic(data.ClinicID);

                const paramsList: Param[][] = [[], [], [], []];
                for (let i = 1; i <= paramsList.length; i++) {
                    const paramList = data[`__list_${i}`];

                    if (paramList) {
                        paramsList[i - 1]  = this.buildParam(paramList);
                    }
                }

                if (data.PasswordWarning && data.PasswordWarning !== '') {
                    this.toastController.create({
                        message: data.PasswordWarning,
                        color: 'warning',
                        buttons: [
                            {
                                role: 'cancel',
                                icon: 'close',
                            }
                        ]
                    }).then(toast => toast.present());
                }

                this.clinicService.update({
                    paramI: paramsList[0],
                    paramII: paramsList[1],
                    paramIII: paramsList[2],
                    paramIV: paramsList[3],
                    sessionList: data.__list_0
                });

                this.sessionService.update({
                    userId: data.UserID,
                    userName: data.UserName,
                    userViewForeignApp: data.UserForeignAppDisabled !== '-1', // user can view other's apps if param is 0
                    language,
                    countries: createCountries(data.__list_7),
                    version: inf.version,
                    clientContactDataView: data.ClientContactDataView !== '0',
                });

                this.settingsStore.update({
                    appUser: data[SET_APP_USER] !== '0',
                    cabinetRequiresDCD: data[SET_LASER_DCD] !== '0',
                    cabinetRequiresShot: Number(data[SET_LASER_SHOT]) as LASER_SHOT,
                    cabinetSkip: data[SET_CABINET_SKIP] !== '0',
                    clientAddressSplit: data[SET_CLIENT_ADDRESS_SPLIT] !== '0',
                    clientCustomFieldsEnable: data[SET_CLIENT_CUSTOMFIELDS_ENABLE],
                    clientCustomFieldsLabels: data[SET_CLIENT_CUSTOMFIELDS_LABELS],
                    clientCustomFieldsTypes: data[SET_CLIENT_CUSTOMFIELDS_TYPE_SELECTED],
                    clientEditRequiredFields: data[SET_CLIENT_EDIT_REQUIRED_FIELDS],
                    clientLegalRepAge: Number(data[SET_LEGAL_REPRESENTATIVE_AGE]),
                    clientHideFields: data[SET_CLIENT_EDIT_HIDE_FIELDS],
                    clientNewFields: data[SET_CLIENT_NEW_FIELDS],
                    clientNewRequiredFields: data[SET_CLIENT_NEW_REQUIRED_FIELDS],
                    clientPicturesDownscale: data[SET_CLIENT_PICTURES_DOWNSCALE] !== '0',
                    clientValidateAppRequiredFields: data[SET_VALIDATE_APP_CLIENT_REQUIRED_FIELDS],
                    combineFactor: data[SET_APP_COMBINE_FACTOR],
                    diaryFutureValidate: data[SET_DIARY_FUTURE_VALIDATE] !== '0',
                    laserSignEnabled: data[SET_LASER_SIGN_ENABLED] !== '0',
                    maxServices: data[SET_APP_MAXSERVICES] === '0' ? Infinity : Number(data[SET_APP_MAXSERVICES]),
                    nameSplit: data[SET_NAME_SPLIT] !== '0',
                    orders: data[SET_ORDERS] !== '0',
                    productsPack: data[SET_PRODUCTS_PACK] !== '0',
                    requiredSelectUser: data[SET_APP_USER_FORCE] !== '0',
                    settingsLoaded: true, // mark as true to know resolver must not load this
                    stock: data[SET_STOCK] !== '0',
                    subscriptions: data[SET_SUBSCRIPTIONS] !== '0',
                    timeSlot: Number(data[SET_TIMESLOT]),
                    duplicateMailUnique: Number(data[SET_CLIENT_MAIL_UNIQUE]) as UNIQUE_RESTRICTIONS,
                    duplicateDniUnique: Number(data[SET_CLIENT_DNI_UNIQUE]) as UNIQUE_RESTRICTIONS,
                    duplicatePhoneUnique: Number(data[SET_CLIENT_PHONE_UNIQUE]) as UNIQUE_RESTRICTIONS,
                    RGPDDisable: data[SET_RGPD_DISABLE] !== '0',
                    visualDoctor: data[SET_DOCTOR_VISUAL] !== '0',
                    clientPrescription: data[SET_CLIENT_PRESCRIPTIONS] !== '0',
                    appNotAssisted: data[SET_APP_NOTASSISTED] !== '0',
                    appNotAssistedFuture: data[SET_APP_NOTASSISTED_FUTURE] !== '0',
                    fwaUserMode: data[SET_APP_FWA_USER_MODE] !== '0',
                    voucherPackAuto: data[SET_VOUCHER_PACK_AUTO] !== '0',
                    traceabilityIsActive: data[SET_STOCK_TRACEABILITY] === '-1',
                    diaryTypeProfessional: data[SET_DIARY_TYPE_DEFAULT] === '-1',
                });

                this.permissionsStore.update({
                    createMultipleAppointments: data[PERM_CREATE_MULTIPLE_APPOINTMENT] === '-1'
                });

                // eslint-disable-next-line max-len
                const doubleDisplay = this.settingsQuery.getValue().diaryTypeProfessional ? DOUBLE_VISUAL_AGENDA.PROFESSIONALS : DOUBLE_VISUAL_AGENDA.CABINETS;
                this.agendaService.setDoubleDisplay(doubleDisplay);
            })
        );
    }

    private buildParam(paramList: any[]): Param[] {
        const listParam: Param[] = [];

        paramList.forEach(p => {
            listParam.push({
                id: p.id,
                name: p.name,
                equipmentId: p.equipmentid
            });
        });

        return listParam;
    }
}
